import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import useHistoryState from 'use-history-state';
import client from 'lib/CommonApi/client';
import {
  callAlertWeb,
  callNativeWeb,
  clickMoveToAppWeb,
  footerShowEventWeb,
  hideProgressWeb,
  showProgressWeb,
  showKycRequiredPopupWeb,
  resumeUploadIDCardWeb, callAppLoginWeb
} from 'common/appInterface';
import { handleApplyInfoError } from 'common/applyInfoError';
import { CardProductCode, CardProductTabName, KYC_REQUIRED_POPUP_MIN_VERSION, QUICK_CARD_REGISTER_MIN_VERSION, APP_UPDATE_REQUIRED_MIN_VERSION, NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION } from '../../constants/Card';
import { WEBVIEW_RESOURCE_VERSION } from 'common/cacheOverwrite';
import BottomModal from 'components/BottomModal/BottomModal';
import StringUtils from 'common/stringUtils';
import CardApplyBlockModal from 'components/CardApplyBlockModal';
import { fetchRestrictedCountryCodes, isRestrictedCountry } from 'common/cardApplyRestriction';

const BenefitsTabPage: React.FC = () => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Params */
  const { cardProductCode = "" } = useParams();
  /** 다국어처리 */
  const { t, i18n } = useTranslation();

  const screenId = "APPL02";
  const screenName = t("APPL02.title");

  const [productList, setProdcutList] = useState<Map<string, Product>>();

  /** 카드상품 조회 */
  const callProductList = useCallback(async () => {
    showProgressWeb();
    await client.get("/api/app/product", { params: location.state.query }).then(response => {
      let productMap = new Map<string, Product>();
      response.data.response.filter((value: Product) => value.useYn === "Y").forEach((value: Product) => {
        productMap.set(value.cardProductCode, value);
      });
      setProdcutList(productMap);
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location]);

  // 최초실행
  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    // callNativeWeb(screenId, screenName);
    callNativeWeb(screenId, screenName, location.state.apply.linkOpenMenu === "benefitsTab" ? 'Y' : 'N');
    // 하단바 숨기기
    footerShowEventWeb("N");

    console.log("QUERY STATE");
    console.log(location.state);

    callProductList();

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {productList && <BenefitsTabChild productList={productList} cardProductCode={cardProductCode} />}
    </>
  );
};

const BenefitsTabChild: React.FC<{ productList: Map<string, Product>, cardProductCode: string}> = (props) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t } = useTranslation();

  const [cardApply, setCardApply] = useState<Partial<CardApplyStep2>>({ ...location.state.apply });
  // 선택한 카드 탭 — 신청 화면에서 뒤로 돌아왔을 때 유지 (history state 저장, URL 은 처음 진입한 카드 그대로)
  const [cardProductCode, setCardProductCode] = useHistoryState<string>(props.cardProductCode, "benefitsTabCardProductCode");

  const [debitState, setDebitState] = useState<CardDebitState | null>(null);
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [isUpgradeModalView, setIsUpgradeModalView] = useState<boolean>(false);

  const [reissueFee, setReissueFee] = useState(0);
  const [reissuePromotionFee, setReissuePromotionFee] = useState(0);
  const [promotionRate, setPromotionRate] = useState(0);
  /** 발급 제한 국가코드 목록 */
  const [restrictedCountryCodes, setRestrictedCountryCodes] = useState<string[]>([]);
  /** 발급 제한 국가 안내 팝업 */
  const [isApplyBlockModal, setIsApplyBlockModal] = useState<boolean>(false);
  /** 선택한 상품의 미완료 접수상태 (/my 응답 receiveStatusCode — 04·05·06이면 재진입 버튼 문구) */
  const [receiveStatusCode, setReceiveStatusCode] = useState<string>("");
  /** 재진입(04) 신분증 신규 업로드 모드 플래그 */
  const [isIdCardApply, setIsIdCardApply] = useState<boolean>(false);
  /** 재진입(04) 신분증 업로드 중인 접수 정보 */
  const [continueApplyReceiveInfo, setContinueApplyReceiveInfo] = useState<{
    receiveSeq?: string,
    receiveDate?: string,
    cardProductCode?: string,
    designDivisionCode?: string,
    userEngName?: string,
    phoneNo?: string,
    sexDivisionCode?: string,
    birthDate?: string
  }>({});

  // 카드 업그레이드 모달 닫기
  const closeUpgradeModal = useCallback(() => {
    setIsUpgradeModalView(false);
  }, []);

  /** 출금동의여부조회 */
  const callDebitState = useCallback(async () => {
    await client.get('/api/app/manage/debit/state', {
      params: {
        ...location.state.query
      }
    }).then(response => {
      setDebitState(response.data.response);
    }).catch((e: AxiosError) => {
      callAlertWeb('ERROR', '99', e.message);
    });
  }, [location.state.query]);

  // 카드 업그레이드 클릭
  const OnUpgradeCardClick = useCallback(() => {
    // 앱 버전이 카드 신청 가능 버전 미만이면 업데이트 안내 얼럿
    if (Number(location.state.query.apiVersion) < APP_UPDATE_REQUIRED_MIN_VERSION) {
      callAlertWeb("99", "99", t('STAT01.popup05'));
    }
    else {
      showProgressWeb();

      /** 고객정보 조회 */
      client.get("/api/app/apply/info", {
        params: {
          ...location.state.query,
          cardProductCode: cardApply.cardProductCode
        }
      }).then((response) => {
        let result = response.data.response;

        if (response.data.code === "00") {
          // 발급 제한 국가
          if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
            // 업그레이드 바텀시트가 떠 있으면 먼저 닫는다. (바텀시트 z-index 1000 > 팝업 dim 300)
            setIsUpgradeModalView(false);
            setIsApplyBlockModal(true);
            return;
          }

          navigate('/apply/cardDesignSelect', {
            state: {
              ...location.state,
              query: {
                ...location.state.query,
                cardProductCode: cardApply.cardProductCode
              },
              apply: {
                ...cardApply,
                userEngName: result.userEngName,
                phoneNo: result.phoneNo,
                sexDivisionCode: result.sexDivisionCode,
                birthDate: result.birthDate,
                foreignerDivisionCode: result.foreignerDivisionCode
              },
              userInfo: debitState
            }
          });
        }
        else {
          handleApplyInfoError(response.data.code, response.data.message, location.state.query.apiVersion);
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
      });
    }
  }, [location.state, navigate, debitState, cardApply, restrictedCountryCodes, t]);

  /** backKeyEvent_App 처리 (APP > WEB) */
  const callbackBackkeyEvent = useCallback(() => {
    if (isUpgradeModalView) {
      closeUpgradeModal();
    } else {
      navigate(-1);
    }

  }, [navigate, closeUpgradeModal, isUpgradeModalView]);

  /** 퀵카드 등록 (기존 동작) */
  const runQuickCardRegister = useCallback(() => {
    if (Number(location.state.query.apiVersion) >= QUICK_CARD_REGISTER_MIN_VERSION) {
      clickMoveToAppWeb("N876");
    } else {
      callAlertWeb("99", "99", t('STAT01.popup05'));
    }
  }, [location.state.query.apiVersion, t]);

  /** 카드신청 계속 (재진입, 메인·상품상세와 동일) — 접수상태별 단계로 이동 */
  const clickApplyContinue = useCallback((cardApplyNotify: CardApplyNotify) => {
    showProgressWeb();

    /** 고객정보 조회 */
    return client.get("/api/app/apply/info", { params: { ...location.state.query, cardProductCode: cardApplyNotify.cardProductCode }}).then((response) => {
      let result = response.data.response;

      if (response.data.code === "00") {
        // 발급 제한 국가
        if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
          setIsApplyBlockModal(true);
          return;
        }

        // KYC 미인증
        if (result.kycApproved === "N") {
          if (Number(location.state.query.apiVersion) >= KYC_REQUIRED_POPUP_MIN_VERSION) {
            showKycRequiredPopupWeb();
          } else {
            callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
          }
          return;
        }

        if (Number(cardApplyNotify.cardProductCode) === CardProductCode.QuickCard) {
          runQuickCardRegister();
          return;
        }

        const query = { ...location.state.query, cardProductCode: cardApplyNotify.cardProductCode };

        // 핀코드 입력 완료 단계
        if (cardApplyNotify.receiveStatusCode === "04") {
          if (parseInt(location.state.query.apiVersion) >= NEW_IDCARD_UPLOAD_SCREEN_MIN_VERSION) {
            // 신규 버전: 네이티브 재진입 신분증 업로드 호출 (결과는 idUpload 이벤트로 수신)
            setIsIdCardApply(true);
            setContinueApplyReceiveInfo({
              receiveSeq: cardApplyNotify.receiveSeq,
              receiveDate: cardApplyNotify.receiveDate,
              cardProductCode: cardApplyNotify.cardProductCode,
              designDivisionCode: cardApplyNotify.designDivisionCode,
              userEngName: result.userEngName,
              phoneNo: result.phoneNo,
              sexDivisionCode: result.sexDivisionCode,
              birthDate: result.birthDate
            });
            resumeUploadIDCardWeb();
            return;
          }
          // 구버전: 신분증 업로드 단계로 이동
          navigate("/apply/step4", {
            state: {
              ...location.state,
              query: query,
              apply: {
                ...cardApply,
                cardProductCode: cardApplyNotify.cardProductCode,
                designDivisionCode: cardApplyNotify.designDivisionCode,
                receiveSeq: cardApplyNotify.receiveSeq,
                receiveDate: cardApplyNotify.receiveDate,
                foreignerDivisionCode: result.foreignerDivisionCode,
                isReupload: false,
                isChangeDelivery: false,
                isContinue: true,
                // 중단 팝업에서 재진입 취소 시 혜택 탭(선택한 카드 탭)으로 돌아오기 위한 정보
                continueFrom: 'benefitsTab',
                continueFromHistoryIdx: window.history.state?.idx,
                userEngName: result.userEngName,
                phoneNo: result.phoneNo,
                sexDivisionCode: result.sexDivisionCode,
                birthDate: result.birthDate
              },
              userInfo: debitState
            }
          });
        }
        // 신분증 업로드 완료 단계는 고객정보등록 단계로 이동
        else if (cardApplyNotify.receiveStatusCode === "05") {
          navigate("/apply/step5", {
            state: {
              ...location.state,
              query: query,
              apply: {
                ...cardApply,
                cardProductCode: cardApplyNotify.cardProductCode,
                designDivisionCode: cardApplyNotify.designDivisionCode,
                receiveSeq: cardApplyNotify.receiveSeq,
                receiveDate: cardApplyNotify.receiveDate,
                isChangeDelivery: false,
                isContinue: true,
                // 중단 팝업에서 재진입 취소 시 혜택 탭(선택한 카드 탭)으로 돌아오기 위한 정보
                continueFrom: 'benefitsTab',
                continueFromHistoryIdx: window.history.state?.idx,
                userEngName: result.userEngName,
                phoneNo: result.phoneNo,
                sexDivisionCode: result.sexDivisionCode,
                birthDate: result.birthDate,
                foreignerDivisionCode: result.foreignerDivisionCode
              },
              userInfo: debitState
            }
          });
        }
        // 카드수령지 선택 단계는 수령지 선택으로 이동
        else if (cardApplyNotify.receiveStatusCode === "06") {
          navigate("/apply/step6/method", {
            state: {
              ...location.state,
              query: query,
              apply: {
                ...cardApply,
                cardProductCode: cardApplyNotify.cardProductCode,
                designDivisionCode: cardApplyNotify.designDivisionCode,
                receiveSeq: cardApplyNotify.receiveSeq,
                receiveDate: cardApplyNotify.receiveDate,
                isChangeDelivery: false,
                isContinue: true,
                // 중단 팝업에서 재진입 취소 시 혜택 탭(선택한 카드 탭)으로 돌아오기 위한 정보
                continueFrom: 'benefitsTab',
                continueFromHistoryIdx: window.history.state?.idx
              },
              userInfo: debitState
            }
          });
        }
        // 그 이전 단계는 처음부터 시작
        else {
          navigate("/apply/step1", {
            state: {
              ...location.state,
              query: query,
              apply: {
                ...cardApply,
                cardProductCode: cardApplyNotify.cardProductCode,
                userEngName: result.userEngName,
                phoneNo: result.phoneNo,
                sexDivisionCode: result.sexDivisionCode,
                birthDate: result.birthDate,
                foreignerDivisionCode: result.foreignerDivisionCode,
                skipCardBenefit: true
              },
              userInfo: debitState
            }
          });
        }
      }
      else {
        handleApplyInfoError(response.data.code, response.data.message, location.state.query.apiVersion);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
    });
  }, [location, navigate, cardApply, debitState, restrictedCountryCodes, runQuickCardRegister, t]);

  /** 카드신청 클릭 */
  const clickApply = useCallback(() => {
    // 앱 버전이 카드 신청 가능 버전 미만이면 업데이트 안내 얼럿 (퀵카드 제외)
    if (Number(cardProductCode) !== CardProductCode.QuickCard
          && Number(location.state.query.apiVersion) < APP_UPDATE_REQUIRED_MIN_VERSION) {
      callAlertWeb("99", "99", t('STAT01.popup05'));
    }
    else {
      showProgressWeb();

      const isQuickCard = Number(cardProductCode) === CardProductCode.QuickCard;

      // 이전 접수내역 확인 (메인·상품상세와 동일)
      client.get("/api/app/product/check", { params: { ...location.state.query, cardProductCode: cardApply.cardProductCode }}).then((response) => {
        let checkResult = response.data.response;

        if (response.data.code !== "00") {
          callAlertWeb("ERROR", response.data.code, response.data.message);
          return;
        }

        // 이전 접수내역이 있으면 재진입
        if (checkResult.receiveStatusNotifyCode === "Y") {
          return clickApplyContinue(checkResult);
        }

        /** 고객정보 조회 */
        return client.get("/api/app/apply/info", {
          params: {
            ...location.state.query,
            cardProductCode: cardApply.cardProductCode
          }
        }).then((response) => {
          let result = response.data.response;

          if (response.data.code === "00") {
            // 발급 제한 국가
            if (isRestrictedCountry(result.countryCode, restrictedCountryCodes)) {
              setIsApplyBlockModal(true);
              return;
            }

            // KYC 미인증
            if (result.kycApproved === "N") {
              if (Number(location.state.query.apiVersion) >= KYC_REQUIRED_POPUP_MIN_VERSION) {
                showKycRequiredPopupWeb();
              } else {
                callAlertWeb("ERROR", "99", t("APPL02.kycApproved"));
              }
              return;
            }

            if (isQuickCard) {
              runQuickCardRegister();
              return;
            }

            // 카드 재발급으로 들어온 경우 약관동의 없이 디자인 선택으로 이동
            navigate(cardApply.isReissue ? "/apply/cardDesignSelect" : "/apply/step1", {
              state: {
                ...location.state,
                query: {
                  ...location.state.query,
                  cardProductCode: cardApply.cardProductCode
                },
                apply: {
                  ...cardApply,
                  userEngName: result.userEngName,
                  phoneNo: result.phoneNo,
                  sexDivisionCode: result.sexDivisionCode,
                  birthDate: result.birthDate,
                  foreignerDivisionCode: result.foreignerDivisionCode,
                  skipCardBenefit: true
                }
              }
            });
          }
          else {
            handleApplyInfoError(response.data.code, response.data.message, location.state.query.apiVersion);
          }
        });
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
      });
    }
  }, [location, navigate, cardApply, t, cardProductCode, restrictedCountryCodes, clickApplyContinue, runQuickCardRegister]);

  /** 재진입 신분증 업로드 정보 초기화 */
  const initContinueApplyReceiveInfo = useCallback(() => {
    setIsIdCardApply(false);
    setContinueApplyReceiveInfo({});
  }, []);

  /** 재진입(04) 신분증 업로드 결과 처리 (completedPhotoID_App (APP > WEB)) — 메인 재진입과 동일 */
  const callbackIDCardUpload = useCallback(async (event) => {
    if (!isIdCardApply) {
      return;
    }

    // App 에서 넘어온 신분증 이미지 세팅
    let idcardList = { [event.detail.idType + event.detail.photoType]: decodeURIComponent(event.detail.imageValue) };
    // 신분증구분코드 세팅 (1: 주민등록증, 3: 외국인 등록증, 4: 여권)
    let idcardDivisionCode = event.detail.idType;

    showProgressWeb();
    await client.post("/api/app/apply/idcard/upload", { ...location.state.query, receiveSeq: continueApplyReceiveInfo.receiveSeq, receiveDate: continueApplyReceiveInfo.receiveDate, idcardDivisionCode: idcardDivisionCode, idcardList: idcardList, idVerifyYn: event.detail.idVerifyYn, idVerifyFailReason: event.detail.disagreementReason }).then((response) => {
      if (response.data.code === "00") {
        navigate("/apply/step5", {
          state: {
            ...location.state,
            query: { ...location.state.query, cardProductCode: continueApplyReceiveInfo.cardProductCode },
            apply: {
              ...cardApply,
              cardProductCode: continueApplyReceiveInfo.cardProductCode,
              designDivisionCode: continueApplyReceiveInfo.designDivisionCode,
              receiveSeq: continueApplyReceiveInfo.receiveSeq,
              receiveDate: continueApplyReceiveInfo.receiveDate,
              userEngName: continueApplyReceiveInfo.userEngName,
              phoneNo: continueApplyReceiveInfo.phoneNo,
              sexDivisionCode: continueApplyReceiveInfo.sexDivisionCode,
              birthDate: continueApplyReceiveInfo.birthDate,
              isChangeDelivery: false,
              // 신분증 업로드로 접수상태 04→05 변경: 이후 중단 시 접수를 취소하지 않는다
              isContinue: false
            },
            userInfo: debitState,
            from: location.pathname
          }
        });
      } else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      initContinueApplyReceiveInfo();
    });
  }, [isIdCardApply, continueApplyReceiveInfo, cardApply, debitState, location.pathname, location.state, navigate, initContinueApplyReceiveInfo]);

  // APP이벤트 공통 수신처리 (APP > WEB)
  useEffect(() => {
    document.addEventListener("backpress", callbackBackkeyEvent);
    return () => document.removeEventListener("backpress", callbackBackkeyEvent);
  }, [callbackBackkeyEvent]);

  useEffect(() => {
    callDebitState();
  }, [callDebitState]);

  // 발급 제한 국가 목록 조회
  useEffect(() => {
    fetchRestrictedCountryCodes(location.state.query.languageCode).then(setRestrictedCountryCodes);

    // 최초한번실행 useEffect() Warning 제거
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inquiryMyCardInfoApi = useCallback(
    async () => {

      let isUpgradeAvailable = false;

      let cardInfo: any = null;

      showProgressWeb();
      setIsUpgradeModalView(false);
      setIsUpgrade(false);
      setReceiveStatusCode("");
      await client.get(`/api/app/product/${cardProductCode}/my`, { params: location.state.query }).then((response) => {
        // 미완료 접수상태 (재진입 버튼 문구용) — TODO 백엔드 /my 응답 receiveStatusCode 추가 예정 (보유 카드가 없는 상품도 응답 필요)
        setReceiveStatusCode(response?.data?.response?.receiveStatusCode ?? "");

        if(response?.data?.response) {
          cardInfo = response?.data?.response;

          if (cardInfo && cardInfo.cardStatusCode) {
            if ((cardInfo.cardStatusCode === "11" || cardInfo.cardStatusCode === "22") && (cardInfo.designDivisionCode === "A" && cardInfo.reissueReceiveYn === "N")) {
              isUpgradeAvailable = true;
            }
          }
        }

        if (isUpgradeAvailable) {
          setReissueFee(cardInfo.reissueFee);
          setPromotionRate(cardInfo.promotionRate);
          setReissuePromotionFee(cardInfo.reissuePromotionFee);

          setIsUpgradeModalView(true);
          setIsUpgrade(true);

          setCardApply({
            ...cardApply,
            cardProductCode: cardProductCode,
            beforeAlternativeCardNo: cardInfo.alternativeCardNo,
            reissueReceiveYn: cardInfo.reissueReceiveYn,
            reissueFee: cardInfo.reissueFee,
            promotionRate: cardInfo.promotionRate,
            reissuePromotionFee: cardInfo.reissuePromotionFee,
            requestIssueDivisionCode: cardInfo.cardStatusCode === "22" ? "24" : "21",
            isReissue: true,
            reissuePromotionYn: "Y"
          });
        } else {
          setIsUpgradeModalView(false);
          setIsUpgrade(false);

          setCardApply({
            ...cardApply,
            cardProductCode: cardProductCode,
            beforeAlternativeCardNo: undefined,
            reissueReceiveYn: undefined,
            reissueFee: undefined,
            isReissue: undefined,
            promotionRate: undefined,
            reissuePromotionFee: undefined,
            reissuePromotionYn: undefined,
            requestIssueDivisionCode: location.state.cardInfoListLength > 0 ? "12" : "11",
          });
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
      });
      // eslint-disable-next-line
    }, [cardProductCode, location.state.query, location.state.cardInfoListLength]);

  useEffect(() => {
    setCardApply((value) => ({ ...value, cardProductCode: cardProductCode }));
    inquiryMyCardInfoApi();
  }, [cardProductCode, inquiryMyCardInfoApi]);

  // 신분증 업로드 이벤트 리스너 (completedPhotoID_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("idUpload", callbackIDCardUpload);
    return () => document.removeEventListener("idUpload", callbackIDCardUpload);
  }, [callbackIDCardUpload]);

  /** 재진입 신분증 업로드 화면에서 신청 취소 (cancelCardApply_App (APP > WEB))
   *    — 접수 취소 후 /my 재조회로 버튼 문구 갱신 (선택한 카드 탭 유지) */
  const callbackCancelCardApply = useCallback(async () => {
    showProgressWeb();
    await client.patch("/api/app/apply/cancel", {
      ...location.state.query,
      receiveSeq: continueApplyReceiveInfo.receiveSeq,
      receiveDate: continueApplyReceiveInfo.receiveDate
    }).then((response) => {
      if (response.data.code === "00") {
        inquiryMyCardInfoApi();
      } else if (response.data.code === "o4") {
        callAlertWeb("ERROR", response.data.code, response.data.message);
        callAppLoginWeb();
      } else {
        callAlertWeb("ERROR", response.data.code, response.data.message);
      }
    }).catch((e: AxiosError) => {
      callAlertWeb("ERROR", "99", e.message);
    }).finally(() => {
      hideProgressWeb();
      initContinueApplyReceiveInfo();
    });
  }, [location.state.query, continueApplyReceiveInfo, inquiryMyCardInfoApi, initContinueApplyReceiveInfo]);

  // 신청 취소 이벤트 리스너 (cancelCardApply_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("cancelCardApply_App", callbackCancelCardApply);
    return () => document.removeEventListener("cancelCardApply_App", callbackCancelCardApply);
  }, [callbackCancelCardApply]);

  useEffect(() => {
    console.log("setIsUpgradeModalView 변경");
    console.log(setIsUpgradeModalView);
  }, [setIsUpgradeModalView]);

  return (
    <div id="wrap">
      <div id="container" className="pcHidden">
        <section className="contents">
          <div className="tabWrap">
            <div className="btnTabWrap">
              {Array.from(props.productList.values()).map((value) => (
                <div
                  key={value.cardProductCode}
                  className={
                    cardProductCode === value.cardProductCode
                      ? 'btnTab on'
                      : 'btnTab'
                  }
                  onClick={() => setCardProductCode(value.cardProductCode, true)}>
                  <button type="button">
                    <span>
                      {CardProductTabName[Number(value.cardProductCode)]}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="inner">
            <div className="section serverWrap">
              <iframe
                className="productTabDetail"
                key={cardProductCode}
                src={`${process.env.REACT_APP_OUTER_HTML_PATH}/card/benefit/${cardProductCode}/detail/card_detail_benefit.html?v=${WEBVIEW_RESOURCE_VERSION}&lang=${location.state.query.languageCode}`}
                allowFullScreen={true}
                title={
                  props.productList?.get(cardProductCode)?.cardProductCodeName
                }
              />
            </div>
          </div>
        </section>
        <div className="btnArea">
          <div className="btnWrapTy01">
            {isUpgrade && (
              <button type="button" className="btnPrimaryTy01"
                      onClick={OnUpgradeCardClick}>
                <span>{t('APPL02.upgradeCard', 'Upgrade Card')}</span>
              </button>
            )}
            {!isUpgrade && (
              <button
                type="button"
                className="btnPrimaryTy01"
                onClick={clickApply}
                disabled={
                  !props.productList ||
                  props.productList?.get(cardProductCode)?.hasYn === 'Y' ||
                  props.productList?.get(cardProductCode)?.receiveYn === 'Y' ||
                  !Array.from(props.productList?.keys()).includes(cardProductCode)
                }>
              <span>
                {props.productList?.get(cardProductCode)?.hasYn === 'Y'
                  ? t('APPL02.exist')
                  : props.productList?.get(cardProductCode)?.receiveYn === 'Y'
                  ? t('APPL02.receive')
                  // 재진입(미완료 접수 04·05·06): 신청 이어하기 문구 (메인과 동일)
                  : ["04", "05", "06"].includes(receiveStatusCode)
                  ? t('APPL01.apply02', 'Continue Card Application')
                  : t('APPL02.apply')}
              </span>
              </button>
            )}
          </div>
        </div>
      </div>
      <BottomModal isOpen={isUpgradeModalView} onClose={closeUpgradeModal}
                   actionLabel={t('APPL02.upgradeCard', 'Upgrade Card')} onAction={OnUpgradeCardClick}>
        <div className={'upgradeBottomModalContainer'}>
          <div className={'upgradeBottomModalTitle'}>
            <span>{t('APPL02.upgradeTitle', 'Issuing Fee Discount')} {promotionRate}%</span>
          </div>
          <div className={'upgradeBottomModalDescription'}>
            <img className={'upgradeNoticeImage'} src={'/assets/images/ico/notice.png'} alt={'Notice Icon'} />
            <span>{t('APPL02.upgradeDescription', 'Any ATM Cash Deposit Available (Including all bank & CVS ATM)')}</span>
          </div>
          <div className={'upgradeBottomModalDescription'}>
            <img className={'upgradeNoticeImage'} src={'/assets/images/ico/notice.png'} alt={'Notice Icon'} />
            <span className={'upgradeBottomModalOldFeeText'}>{StringUtils.moneyFormat(StringUtils.nvlDefault(reissueFee, '0'))} KRW</span>
            <span className={'upgradeBottomModalFeeText'}>{StringUtils.moneyFormat(StringUtils.nvlDefault(reissuePromotionFee, '0'))} KRW</span>
          </div>
        </div>
      </BottomModal>
      <CardApplyBlockModal isOpen={isApplyBlockModal} onClose={() => setIsApplyBlockModal(false)} />
    </div>
  );
};

export default BenefitsTabPage;